import React, { useContext, useRef, useCallback } from 'react';
import { graphql } from 'react-relay';

import type { PresenceActivity } from '@atlaskit/editor-common/collab';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import {
	NativeCollabPresenceStateContainer,
	useNativeCollabPresenceState,
} from '@confluence/native-collab/entry-points/useNativeCollabPresenceState.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import {
	ExperienceTrackerContext,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { useIsGuest } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';

import {
	type PresenceParticipant,
	type PresenceSessionData,
	PresenceStoreContainer,
} from '../presence-store/index.ts';
import type {
	CanvasCollaborators,
	PresenceContentMode,
	PresencePageContentType,
} from '../presenceTypes.ts';
import { CURRENT_USER_DUMMY_SESSION_ID } from '../presenceUtils.ts';
import { useAnonymousAsset } from '../page-presence/useAnonymousAsset.ts';
import { LoadableAfterPaint } from '@confluence/loadable';

import type {
	ContentHeaderPresenceUserQuery as ContentHeaderPresenceUserQuery$data,
	ContentHeaderPresenceUserQueryVariables as ContentHeaderPresenceUserQuery$variables,
	ContentHeaderPresenceUserQuery_user_AtlassianUser,
} from './__types__/ContentHeaderPresenceUserQuery.ts';
import type {
	ContentHeaderPresenceContentSettingsQuery$data,
	ContentHeaderPresenceContentSettingsQuery$variables,
} from './__generated__/ContentHeaderPresenceContentSettingsQuery.graphql.ts';

const PagePresence = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PagePresence" */ '../page-presence/PagePresence.tsx'
			)
		).PagePresence,
});

const CanvasPresence = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CanvasPresence" */ './CanvasPresence.tsx'))
			.CanvasPresence,
});

const ContentHeaderPresenceContentSettingsQuery = graphql`
	query ContentHeaderPresenceContentSettingsQuery($cloudId: ID!, $spaceKey: String!) {
		confluence_teamPresenceContentSetting(cloudId: $cloudId, spaceKey: $spaceKey) {
			isEnabledOnContentView
		}
	}
`;

export type ContentHeaderPresenceProps = {
	collaborators?: CanvasCollaborators | null;
	contentId: string;
	contentType?: string | null;
	isViewMode?: boolean;
	analyticSource?: string;
};

const createDefaultParticipant = ({
	presenceId,
	activity,
	user,
	isGuest,
}: {
	presenceId: string;
	activity: PresenceActivity;
	user?: ContentHeaderPresenceUserQuery_user_AtlassianUser | null;
	isGuest: boolean;
}): PresenceParticipant | undefined => {
	if (!user) {
		return undefined;
	}

	const sessions: PresenceSessionData = {
		[CURRENT_USER_DUMMY_SESSION_ID]: {
			activity,
		},
	};

	const usablePresenceId = user?.id ?? presenceId;

	return {
		id: usablePresenceId,
		userId: user?.id ?? 'unidentified',
		presenceId: usablePresenceId,
		name: user?.displayName ?? '',
		src: user?.photos?.[0]?.value ?? '',
		isCurrentUser: true,
		isGuest,
		sessions,
	};
};

const UFO_SEGMENT_NAME = 'content-header-presence';

export const ContentHeaderPresenceComponent = ({
	collaborators,
	contentId,
	contentType,
	isViewMode,
	analyticSource,
}: ContentHeaderPresenceProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { cloudId } = useSessionData();
	const isGuest = useIsGuest();
	const spaceKey = useSpaceKey();

	const isEditPage = useIsEditorPage();

	const isLivePage = useIsCurrentPageLive();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

	const { fetchAnonymousAsset } = useAnonymousAsset();

	const isCanvasType =
		contentType === 'whiteboard' || contentType === 'database' || contentType === 'slide';
	const { data: userData, error: userQueryError } = useQueryInterop<
		ContentHeaderPresenceUserQuery$data,
		ContentHeaderPresenceUserQuery$variables
	>(graphql`
		query ContentHeaderPresenceUserQuery {
			user: confluence_atlassianUser(current: true) {
				id
				displayName
				photos {
					value
					isPrimary
				}
			}
		}
	`);

	// presenceId is pulled from the native collab state to ensure that we have a consistent presenceId for a given contentId
	// across the collab providers that drive editor as well as presence components (telepointer and avatar colors).
	const [{ presenceId }] = useNativeCollabPresenceState();

	const presenceIdRef = useRef<string>(presenceId);

	let contentMode: PresenceContentMode = 'view';

	if (isLivePage) {
		contentMode = 'live';
	} else if (isEditPage) {
		contentMode = 'edit';
	}

	const activity: PresenceActivity =
		contentMode === 'edit' || (contentMode === 'live' && (isLiveEditMode || !isViewMode))
			? 'editor'
			: 'viewer';

	const { data: settingData, error: settingsQueryError } = useQueryInterop<
		ContentHeaderPresenceContentSettingsQuery$data,
		ContentHeaderPresenceContentSettingsQuery$variables
	>(ContentHeaderPresenceContentSettingsQuery, {
		variables: {
			cloudId,
			spaceKey: spaceKey ?? '',
		},
		skip: isCanvasType || contentMode !== 'view',
	});

	const error = userQueryError || settingsQueryError;

	const isSettingEnabledOnView = useCallback(() => {
		return (
			isCanvasType ||
			settingData?.confluence_teamPresenceContentSetting?.isEnabledOnContentView === true
		);
	}, [isCanvasType, settingData]);

	const handleError = (err: Error) => {
		experienceTracker.fail({
			name: TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
			error: err,
		});

		markErrorAsHandled(err);

		return null;
	};

	if (!contentType) {
		return null;
	}

	if (error) {
		handleError(error);
	}

	if (contentMode === 'view' && !isSettingEnabledOnView()) {
		experienceTracker.abort({
			name: TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
			reason: `Real-time visibility is disabled on view mode`,
		});

		return null;
	}

	return (
		<PresenceStoreContainer
			participant={createDefaultParticipant({
				presenceId: presenceIdRef.current,
				activity,
				user: userData?.user,
				isGuest,
			})}
			fetchAnonymousAsset={fetchAnonymousAsset}
			onError={handleError}
		>
			{isCanvasType ? (
				<CanvasPresence
					collaborators={collaborators}
					contentId={contentId}
					contentType={contentType}
					analyticSource={analyticSource}
				/>
			) : (
				<PagePresence
					contentId={contentId}
					contentMode={contentMode}
					contentType={contentType as PresencePageContentType}
					activity={activity}
					analyticSource={analyticSource}
				/>
			)}
		</PresenceStoreContainer>
	);
};

export const ContentHeaderPresence = (props: ContentHeaderPresenceProps) => {
	return (
		<ErrorBoundary attribution={Attribution.COLLABORATION_PRESENCE}>
			<UFOSegment name={UFO_SEGMENT_NAME}>
				<NativeCollabPresenceStateContainer scope={props.contentId}>
					<ExperienceStart name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} />
					<ContentHeaderPresenceComponent {...props} />
				</NativeCollabPresenceStateContainer>
			</UFOSegment>
		</ErrorBoundary>
	);
};
